import { CommonStudent } from '@codidae/common-types';
import { useQueries } from '@tanstack/react-query';
import AltusTalk from 'components/altus-talk/altus-talk';
import ChaptersPath from 'components/chapters-path/chapters-path';
import ChildrenSelectField from 'components/children-select-field/children-select-field';
import LoadingScreen from 'components/loading-screen/loading-screen';
import { useContext, useEffect, useState } from 'react';
import { getChapters } from 'services/chapters';
import { getChildrenWorkedChapters } from 'services/childrens';
import { getColorFromFieldId } from 'utils/colors';
import { ChildrenHeaderContext } from 'views/children/dashboard';
import styles from './children-worked-chapters.module.scss';

/* eslint-disable-next-line */
export interface ChildrenWorkedChaptersProps {
  children: CommonStudent;
}

export function ChildrenWorkedChapters({ children }: ChildrenWorkedChaptersProps) {
  const [selectedField, setSelectedField] = useState<number | null>(null);
  const { setColor } = useContext(ChildrenHeaderContext);
  const [
    { data: dataWorkedChapters, isLoading: isLoadingWorkedChapters, refetch: refetchWorkedChapters },
    { data: dataChapters },
  ] = useQueries({
    queries: [
      {
        queryKey: ['workedChapters', children.id],
        queryFn: () => getChildrenWorkedChapters(children.id),
      },
      {
        queryKey: ['chapters'],
        queryFn: () => getChapters(),
        staleTime: 24 * 3600000 * 21,
        gcTime: 24 * 3600000 * 21,
      },
    ],
  });

  useEffect(
    () => () => {
      setColor(null);
    },
    [setColor],
  );

  useEffect(() => {
    refetchWorkedChapters();
  }, [children.id, refetchWorkedChapters]);

  useEffect(() => {
    const wrapper = document.querySelector(`.${styles.wrapper}`);
    const notWorked = document.querySelector(`.${styles.notWorked}`);
    if (!wrapper || !notWorked) return;

    console.log(notWorked.getBoundingClientRect().top);

    wrapper.scrollTo({
      top: notWorked.getBoundingClientRect().top - window.innerHeight + 120,
    });
  }, [dataWorkedChapters]);

  if (isLoadingWorkedChapters || !dataWorkedChapters) {
    return <LoadingScreen />;
  }

  if (dataWorkedChapters.length === 0) {
    return (
      <div style={{ marginTop: 80, paddingTop: 60 }}>
        <AltusTalk type="hi">
          Salut ! Je suis Altus, ton assistant personnel. Je suis là pour t&apos;aider à progresser.
          <br />
          <br />
          Réalise un premier quiz pour débloquer tes applications
        </AltusTalk>
      </div>
    );
  }
  const allFields = dataChapters
    ?.filter((chapter) => chapter.cycles.find((cycle) => cycle.id === children.cycle?.id))
    .map((chapter) => chapter.field)
    ?.filter((field, index, self) => self.findIndex((f) => f.id === field.id) === index);
  if (!selectedField) {
    return (
      <ChildrenSelectField
        fields={allFields ?? []}
        onSelect={(fieldId) => {
          setSelectedField(fieldId);
          setColor(getColorFromFieldId(fieldId));
        }}
        workedChapters={dataWorkedChapters}
        chapters={
          dataChapters?.filter((chapter) => chapter.cycles.find((cycle) => cycle.id === children.cycle?.id)) ?? []
        }
      />
    );
  }
  return (
    <ChaptersPath
      allFields={allFields ?? []}
      chapters={dataChapters ?? []}
      child={children}
      onBack={() => setSelectedField(null)}
      selectedFieldId={selectedField}
      workedChapters={dataWorkedChapters}
    />
  );
}

export default ChildrenWorkedChapters;
